import { Config, Helper } from 'config';

import { forEach, map, invokeMap, find, findLast, assign,
         some, every, last, extend, uniqueId } from 'lodash';

import { Base, resolve, whenAll, flatMap, concat, setForEach } from 'gear';

import { Xml } from '../../util/xml';
import { Point, Matrix, Bounds, Frame, BoundsBuilder } from '../../geometry';
import { Figure } from '../figure';
import { AbsoluteAnchor } from '../anchor';
import { PowerClipContent, PowerClipLink } from '../power-clip';
import { Region, mapCurvesToRegions } from './region';
import { Polyregion } from './polyregion';


const _invalidate = function() { this.invalidate(); };

const paintedPolyregion = function(p) {
  return ! p._brush._empty ; // && ! ( p._pen._empty || p._pen._alpha === 0 );
};

const _manageDeprecatedUnarrangedAttr = function(shape,shapeNode) {
  if( shapeNode.getAttribute('FillMode') || shapeNode.getAttribute('IsUnarranged') ) {

    const shapeIsUnarranged = Xml.getBoolAttr(shapeNode, 'IsUnarranged', false);
    const fillMode = shapeNode.getAttribute('FillMode') || 'Alternate';
    const polyregions = shape._polyregions;
    if( shapeIsUnarranged ) {
      const renderFrame = polyregions[0]._renderFrame;
      let curves = [] ;
      forEach( polyregions, p => {
        forEach( p.regions, r => {
          curves = curves.concat(r._curves); // We use _curves to avoid tryigering lazy CDL decoding
        });
      });
      // [TODO-STYLE]
      const unarrangedPolyregion = new Polyregion([new Region(curves)],{fillMode:fillMode,renderFrame:renderFrame});
      polyregions.set( [unarrangedPolyregion] );
    }
    else if( Config.convertShapeFillMode ) {
      // unarranged is false, but fillmode is in the parent
      // Each region should be converted to a polyregion
      const arrangedPolyregions = [];
      forEach( polyregions, p => {
        forEach( p.regions, r => {
          // [TODO-STYLE] p.pen.clone(), p.brush.clone() ),
          arrangedPolyregions.push( new Polyregion([r],{fillMode:fillMode,renderFrame:p._renderFrame,brush:p._brush,pen:p._pen}) );
        });
      });
      polyregions.set( arrangedPolyregions );
    }
  }
};

export const Shape = Figure.Shape = Figure.extend({ typeName: 'Shape',

  type: 'Shape',

  Properties : {
    polyregions : { type: 'Polyregion[]', def: [], owned: true, manualParsing: true, onChange: _invalidate }
  },

  get isShape() { return true; },

  _ignoreAttributes : Figure.prototype._ignoreAttributes.concat(['fillMode', 'isUnarranged']),

  invalidate() {
    invokeMap(this.polyregions,'outdate');
    this.callParent();
  },

  /* // [TODO]
  brush( v ) {
    if(v!==undefined) { // Remove all the polyregions pens
      forEach(this._polyregions, p => { p.brush = Brush.empty; });
    }
    return this.callParent(v);
  },
  pen( v ) {
    if(v!==undefined) { // Remove all the polyregions pens
      forEach(this._polyregions, p => { p.pen = Pen.empty; });
    }
    return this.callParent(v);
  },
  */

  getFirstPoint() {
    return this.matrix_().apply( this._polyregions[0].getFirstPoint() );
  },

  buildXml( parentNode ) { Xml.addObject(parentNode,this,'Shape'); },
  setupXml( node ) {
    this.callParent(node);
    const ps = this._polyregions;
    //_.forEach(ps,function(p) {
    for(let k=0,kEnd=ps.length;k<kEnd;++k) {
      ps[k].buildXml(node);
    }//);
  },

  polyregionAtPoint_( point, options ) {
    const transform = this.globalMatrix_();
    // The transform argument here is temporal, we need to better managed the cached
    // geometry information so it is also available for the output figure to use
    const { polyregions } = this;
    for( let k = polyregions.length-1; k >= 0; --k ) {
      const p = polyregions[k];
      if( p.hitTest(point,transform,options) ) {
        return p;
      }
    }
  },

  // Returns an array of curves that are already transformed by the shape transform
  flattenCurves$() {
    var ps = this.polyregions;
    return this.globalMatrix$() .then( shapeMatrix => {
    var flattenCurves = [];
    for(var k=0,kEnd=ps.length;k<kEnd;++k){
      var curves = ps[k].enumCurves();
      for(var c=0,cEnd=curves.length;c<cEnd;++c){
        flattenCurves.push( curves[c].transformed(shapeMatrix) );
      }
    }
    return flattenCurves;
    });
  },

  boundsIsVectorBounds() {
    return this._pen._empty && every( this.polyregions, p => p._pen._empty ) &&
           this._processes.length === 0;
  },

  _primitive_computePreReferencePoint() {
    return this._polyregions[0].getFirstPoint();
  },

  _primitive_computePreVectorBounds() {
    // console.log('    <> Shape._primitive_computePreVectorBounds');
    return Bounds.ofThings(this._polyregions) ;
  },
  _primitive_computePreVectorBoundsAfter( transform ) {
    // console.log('    <> Shape._primitive_computePreVectorBoundsAfter '+transform.toMatrixString());
    return Bounds.ofThingsAfter(this._polyregions,transform);
  },
  _primitive_computePreBounds() {
    return this._primitive_computePreBoundsAfter(new Matrix());
  },
  _primitive_computePreBoundsAfter( transform ) {
    // console.log('    <> Shape._primitive_computePreBoundsAfter '+transform.toMatrixString());
    const shapePen = this._pen ;
    const transformScaleFactor = transform.scaleFactor();
    const boundsBuilder = new BoundsBuilder() ;
    const ps = this._polyregions;
    //_.forEach(ps,function(p) {
    for(let k=0,kEnd=ps.length;k<kEnd;++k) {
      const p = ps[k];
      const pen = p.combinedPen(shapePen,true) ;
      const bounds = p.boundsAfter(transform).clone() ;
      if( pen._width > 0 ) {
        // Add half the pen width to each side of the bounds
        const pwc = 0.5*(pen._width*transformScaleFactor) ;
        bounds.inflate(pwc,pwc) ;
      }
      boundsBuilder.update(bounds);
    }//);
    return boundsBuilder.bounds;
  },

  _primitive_computePreMaterialBoundsAfter : function(transform) {
    // console.log('    <> Shape._primitive_computePreBoundsAfter '+transform.toMatrixString());
    var shapePen = this._pen;
    var shapeBrush = this._brush;
    var transformScaleFactor = transform.scaleFactor();
    var boundsBuilder = new BoundsBuilder() ;
    var ps = this._polyregions;

    //_.forEach(ps,function(p){
    for(var k=0,kEnd=ps.length;k<kEnd;++k){ var p = ps[k];
      var brush = p.combinedBrush(shapeBrush);
      var pen = p.combinedPen(shapePen,true) ;

      var realBrush = ! ( brush._color && brush._color.showThrough );
      var realPen = pen._width > 0 && ! pen._color.showThrough;

      // console.log( realBrush + ' ' + realPen );
      if( realBrush || realPen ) {

        var bounds = p.boundsAfter(transform).clone() ;
        if( realPen ) {
          // Add half the pen width to each side of the bounds
          var pwc = 0.5*(pen._width*transformScaleFactor) ;
          bounds.inflate(pwc,pwc) ;
        }
        boundsBuilder.update(bounds);
      }
    }//);
    return boundsBuilder.bounds;
  },

  _preparedToDraw( zoom ) {
    return every( this.polyregions, p => {
      const brush = p.rwBrush();
      if( 'preparedToDraw' in brush ) {
        // [TODO] Abstract
        const matrix = p.globalMatrix_(Matrix.scale(zoom));
        const frame = ( p.renderFrame || p.initialRenderFrame() ).transformed(matrix);
        return brush.preparedToDraw(zoom,matrix,frame);
      }
      return true;
    });
  },

  _prepareToDraw$( zoom ) {
    let promises = [];
    const polyregions = this._polyregions;
    for(let k=0,kEnd=polyregions.length;k<kEnd;++k) {
      const p = polyregions[k];
      const brush = p.rwBrush();
      if( 'prepareToDraw$' in brush ) {
        // [TODO] Abstract
        const matrix = p.globalMatrix_(Matrix.scale(zoom));
        const frame = ( p.renderFrame || p.initialRenderFrame() ).transformed(matrix);
        promises.push( brush.prepareToDraw$(zoom,matrix,frame) );
      }
    }
    return whenAll(promises);
  },

  quickToDraw() {
    // We can do something better later here, maybe count points
    const polyregions = this._polyregions;
    return polyregions.length === 1 && polyregions[0]._regions.length < 10 &&
           this._powerClipContents.length === 0 &&
           this._brush.quickToDraw() && polyregions[0]._brush.quickToDraw();
  },

  processesAreSupported() {
    const { processes } = this;
    return processes.length === 0 || every( processes, process => process.canProcess(this) );
  },

  canBeProcessedLocally() {
    return this._representationType() === 'local' ;
  },

  fillRuleSupported() {
    if( Helper.isIE_let(10) ) {
      // IE9 and IE10 doesn't support HTML5 Canvas fillRule evenodd
      const { polyregions } = this;
      for(let k = 0, kEnd = polyregions.length; k<kEnd; ++k ) {
        if( polyregions[k]._fillMode === 'Alternate' ) {
          return false;
        }
      }
    }
    return true;
  },

  needsCacheToRender() {
    if( Config.alwaysRenderLocally ) {
      return false;
    }
    if( ! Config.renderLocallyWhenPossible ) {
      return true;
    }
    return ! ( this.powerClipsLocalRendering() &&
               this.fillRuleSupported());
  },

  _representationType() {
    if( Config.alwaysRenderLocally ) {
      return 'local' ;
    }
    if( ! Config.renderLocallyWhenPossible ) {
      return 'server' ;
    }
    if( this.processesAreSupported()    &&
        this.powerClipsLocalRendering() &&
        this.fillRuleSupported()) {
      return 'local';
    }
    else {
      return 'server' ;
    }
  },

  drawForClipping( context, transform ) {
    const cps = this._polyregions;
    for(let k=0,kEnd=cps.length;k<kEnd;++k) { 
      const p = cps[k];
      p.drawForClipping(context, transform);
    }
  },

  _draw( context, transform, config, overlayedNoClip = false ) {
    if (this.clippingHidden()) {
      return;
    }
    context.save();

    if (!overlayedNoClip) {
      const [lClipContent, lClipParent] = this.getClippedBy();
      if (lClipContent) {
        if (lClipContent.editingClip) {
          this._draw(context, transform, config, true);
        }
        let lOldTransform = transform;
        if (!lClipContent.clipAbsolute && !lClipContent.editingClip) {
          transform = Matrix.m(transform, lClipParent.globalMatrix_());
        }
        lClipContent.clipContext(context, transform);
        //DEBUG
        // const ps = lClipContent._polyregions;
        // for(let k=0,kEnd=ps.length;k<kEnd;++k) { const p = ps[k];
        //   p.draw(context,transform,config);
        // }
        //END DEBUG
        transform = lOldTransform;    
      }
    }    

    if (overlayedNoClip) {      
      context.globalAlpha = 0.2;
    }
    const ps = this._polyregions;
    //_.forEach(ps,function(p) {
    for(let k=0,kEnd=ps.length;k<kEnd;++k) { const p = ps[k];
      p.draw(context,transform,config);
    }//);
    
    context.restore();

  },

  // The transform argument here is temporal, we need to better managed the cached
  // geometry information so it is also available for the output figure to use
  _fineHitTest( point,transform,options) {
    const { polyregions } = this;
    //_.some(ps,...)
    for(let k=polyregions.length-1,kEnd=0;k>=kEnd;--k) {
        if( polyregions[k].hitTest(point,transform,options) ) {
        if( options ) {
          options.polyregionHit = polyregions[k];
        }
        return true;
      }
    }
    return false;
  },

  // [TODO][PROP]
  powerClip( v ) { if(v===undefined) { return this._powerClipContents[0]; }
    this.powerClipContents = v ;
    setForEach(this.polyregions,'powerClipId',v.id);
    return this;
  },

  addPowerClipContents( v ) {
    const pcContents = this._powerClipContents;
    const polyregions = this._polyregions;
    let p = findLast(polyregions,'_powerClipId');
    if( !p ) {
      setForEach(this.polyregions,'powerClipId','PowerClip-0');
      p = last(polyregions);
    }
    const id = p._powerClipId || uniqueId("PC");
    const pccN = PowerClipContent.createForFigure(id, v, this);
    const pcc = find(pcContents,'_id',id);
    if( pcc ) {
      pcc._figures._add( pccN._figures._clear() );

      if( this.usingPowerClipLink() ) {
        this.invalidateView();
      }
      else {
        this.invalidate();
      }
    }
    else {
      pcContents._add(pccN);

      const o = this._outputFigure;
      if( o && o === this && every( v, f => ! f.needsCacheToRender() ) ) {
        this.invalidateView();
      }
      else {
        this.invalidate();
      }
    }

    return this;
  },

  breakApartPolyregions() {
    const _this = this;
    const fc = _this.clone();
    const polyregions = fc._polyregions;
    // fc.polyregions = [];
    const prePin = _this._computeInitialPrePin();
    return map( polyregions, p => {
      const fp = fc.clone();
      fp.polyregions = [p];
      fp.anchor = AbsoluteAnchor(prePin); // [TODO] Better anchoring?
      return fp;
    });
  },

  regroupShapes( shapes) {

    const prePin = this._computeInitialPrePin();
    const this_matrix_inv = this.matrix_().inverted();

    const new_polyregions = flatMap( shapes, shape => {
      const matrix = Matrix.m( this_matrix_inv, shape.matrix_() );
      return map( shape.polyregions, polyregion => {
        const cp = polyregion.clone();
        cp.transform(matrix);
        if( cp._brush._empty ) {
          cp.brush = shape._brush;
        }
        if( cp._pen._empty ) {
          cp.pen = shape._pen;
        }
        return cp;
      });
    });

    this.anchor = AbsoluteAnchor(prePin);
    this.polyregions = new_polyregions;

    return this;
  },

  otherColors( colors ) {
    forEach( this.polyregions, p => {
      p.brush.getColors(colors);
      p.pen.getColors(colors);
    });
    return colors;
  },

  getPens( pens = [] ) {

    // In the special case of shape, an empty pen means no default
    // The important pens are the ones in the polyregions
    if( ! this._pen.empty() ) {
      pens.push( this._pen );
    }
    this._processes.getPens(pens);
    this.otherPens(pens);

    const pccs = this._powerClipContents;
    for(let k=0,kEnd=pccs.length;k<kEnd;++k) {
      pccs[k].getPens(pens);
    }

    const prs = this._polyregions;
    for(let k=0,kEnd=prs.length;k<kEnd;++k) {
      const lPen = prs[k].pen;
      if (!lPen.empty()) {
        pens.push(lPen);
      }
    }

    return pens;
  },

  getBrushes( brushes = [], forUniqueZones = false ) {

    // In the special case of shape, an empty brush means no default
    // The important brushes are the ones in the polyregions
    const defaultBrush = this._brush, defaultBrush_empty = defaultBrush._empty;

    const polyregionBrushes = this.otherBrushes([],forUniqueZones);

    const polyregions = this._polyregions;

    const ignoreDefaultBrush = forUniqueZones && ( defaultBrush_empty ||
                             every(polyregions,paintedPolyregion) );

    const ignorePolyregionBrushes = ! ignoreDefaultBrush &&
                                  forUniqueZones && ! defaultBrush_empty &&
                                  ! some(polyregions,paintedPolyregion);

    if( ! ignoreDefaultBrush ) {
      brushes.push( defaultBrush );
    }

    this._processes.getBrushes(brushes);

    const { powerClipContents } = this;
    for(let k=0,kEnd=powerClipContents.length;k<kEnd;++k) {
      powerClipContents[k].getBrushes(polyregionBrushes,forUniqueZones);
    }

    if( ! ignorePolyregionBrushes ) {
      for(let i=0,iEnd=polyregionBrushes.length;i<iEnd;++i) {
        brushes.push(polyregionBrushes[i]);
      }
    }

    return brushes;
  },

  otherBrushes( brushes, forUniqueZones ) {
    const ps = this._polyregions;
    //_.forEach(ps,function(p) {
    for(let k=0,kEnd=ps.length;k<kEnd;++k) {
      const p = ps[k];
      const brush = p._brush;
      if( ! forUniqueZones || ! brush._empty ) {
        brushes.push(brush);
      }
    }//);
    return brushes;
  },

  _computeInitialPrePin() {
    const ag = this._anchor;
    const pos = ag._pos.clone();
    return ag._absolute ? pos : this._primitive_computePreVectorBounds().interpolate( pos ) ;
  },

  powerClipLinkClear() {
    this.callParent();
    const ps = this._polyregions;
    //_.forEach(ps,function(p) {
    for(let k=0,kEnd=ps.length;k<kEnd;++k) {
      ps[k]._powerClipLink = null;
    }//);
  },

  inputPrimitiveFigures$() {

    return resolve({
      figures: [ this.toGShape() ],
      anchor: this._computeInitialPrePin()
    });
  },

  toPrimitive() {
    const s = this.toGShape();
    s.transform( this.matrix_(), this._computeInitialPrePin() );
    return s;
  },

  canEditContent() {
    return this._processes.length === 0;
  },

  // This is not a good name, it is not including matrix22 and pin
  // Maybe... preGShape
  toGShape() {
    const _this = this, this_polyregions = _this._polyregions, powerClipContents = _this._powerClipContents;
    const frame = Frame.fromBounds(Bounds.ofThings(this_polyregions));
    const polyregions = map( this_polyregions, p => {
      const cp = p.clone();
      if( cp._brush._empty ) {
        cp.brush = _this._brush.clone();
      }
      if( cp._pen._empty ) {
        cp.pen = _this._pen.clone();
      }
      cp._renderFrame = frame.clone();

      // Keep a PowerClipLink in the polyregion
      const pcId = p._powerClipId;
      if( pcId ) {
        const pcContent = find(powerClipContents,{_id:pcId});
        if( pcContent ) {
          cp._powerClipLink = new PowerClipLink({ content: pcContent });
        }
      }

      return cp;
    });
    return new GShape({polyregions:polyregions});
  }
});

Shape.parseXml = function(shapeNode,bcFixes) {

  const shape = new Shape();

  Figure._baseParseXml.call( shape, shapeNode, Shape, bcFixes ); // call the parent

  shape.polyregions = Polyregion.listFromXml(shapeNode);

  // Backward compatibility, use unarrange and fillmode at the polyregion level
  _manageDeprecatedUnarrangedAttr(shape,shapeNode);

  return shape;
};

Shape.CreateRectangle = function( config ) {
  const center = config.center || ( config.bounds ? config.bounds.center : new Point(0,0) ) ;
  const polyregion = Polyregion.CreateRectangle(config);
  const shape = new Shape({
    anchor: AbsoluteAnchor(center.clone()),
    pin: center,
    polyregions: [polyregion]
  });
  config.center = center;
  return shape;
};

Shape.CreatePolyline = function( config ) {
  const center = Bounds.ofPoints(config.points).center;
  const polyregion = Polyregion.CreatePolyline(config);
  const shape = new Shape({
    anchor: AbsoluteAnchor(center.clone()),
    pin: center,
    polyregions: [polyregion]
  });
  return shape;
};

const createFromPolyregions =
Shape.createFromPolyregions = function(polyregions,config) {
  const center = Bounds.ofThings(polyregions).center;
  return Shape(assign({
    polyregions: polyregions,
    anchor: AbsoluteAnchor(center),
    pin: center.clone()
  },config));
};

const createFromPolyregion =
Shape.createFromPolyregion = function(polyregion,config) {
  return createFromPolyregions([polyregion],config);
};

const createFromCurves =
Shape.createFromCurves = function(curves,config ) {
  var p = {
    fillMode: 'Winding',
    regions: mapCurvesToRegions(curves)
  };
  extend(p,config||{}); // pen and brush
  return createFromPolyregion( Polyregion(p) );
};

Shape.createFromCurve = function(curve,config ) {
  return createFromCurves([curve],config);
};


// Internal Shape for processesing

export const GShape = Base.extend({ typeName: 'GShape',

  Properties : {
    polyregions : { type: 'Polyregion[]', def: [], owned: true },
  },

  get isGShape() { return true; },

  transform( t, prePin ) { // [TODO][PC]
    const ps = this._polyregions;

    for(let k=0,kEnd=ps.length;k<kEnd;++k) {
      const p = ps[k];

      p.transform(t);

      // This just transforms the power clip link, not the content
      const powerClipLink = p._powerClipLink;
      if( powerClipLink && prePin ) {
        const tpp = Matrix.m(t,Matrix.translate(prePin));
        powerClipLink.transform(tpp);
      }
    }
  },

  vectorBounds() {
    return Bounds.ofThings(this._polyregions) ;
  },

  get bounds() {
    const boundsBuilder = new BoundsBuilder() ;
    const ps = this._polyregions;
    for(let k=0,kEnd=ps.length;k<kEnd;++k) {
      const p = ps[k];
      const bounds = p.bounds;
      const pen = p.pen;
      const renderMatrix = p._renderMatrix;
      const pen_width = pen._width * ( renderMatrix ? renderMatrix.scaleFactor() : 1 );
      if( pen_width > 0 ) {
        // Add half the pen width to each side of the bounds
        const pwc = 0.5*pen_width;
        bounds.inflate(pwc,pwc);
      }
      boundsBuilder.update(bounds);
    }
    return boundsBuilder.bounds;
  },

  removePowerClips() {
    const ps = this._polyregions;
    for(let k=0,kEnd=ps.length;k<kEnd;++k) {
      const p = ps[k];
      p._powerClipId   = null;
      p._powerClipLink = null;
    }
  },

  setRenderFrame( frame) {
    const ps = this._polyregions;
    for(let k=0,kEnd=ps.length;k<kEnd;++k) {
      ps[k]._renderFrame = frame.clone();
    }
  },

  invalidate() {
    // ...
  }
});

export const toSingleGShape = function( figures, keepRenderMatrix = false ) {
  let polyregions = [];
  for(let k = 0, kEnd = figures.length; k < kEnd; ++k ) {
    const figure = figures[k];
    if( figure.isGShape ) {
      polyregions = concat(polyregions,figure.polyregions);
    }
    else {
      const gs = figure.toGShape();
      polyregions = concat(polyregions,gs.polyregions);
    }
  }
  const lRet = new GShape({polyregions:polyregions});
  if (keepRenderMatrix) {
    for( var c=0, cEnd = polyregions.length; c < cEnd; ++c ) {
      lRet.polyregions[c].renderMatrix = polyregions[c].renderMatrix;
    }
  }
  return lRet;
};



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/shape/shape.js
 **/